import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { tourStepsFullScreenDisplay } from "./tourStepsFullScreenDisplay";
import GuidedTour from "../../components/GuidedTour";
import FeedbackForm from "../../components/FeedbackForm";
import { useTour } from "../../context/TourContext";
import FeedbackIcon from "../../components/FeedbackIcon";
import Confetti from "react-confetti";
import { useCompletion } from "../../context/CompletionContext";
import { SessionStats } from "../../api";
import { isNumberSenseQuestion } from "../../utils/RandomGenerators";
import {
  expandPictureQuizToSlides,
  PICTURE_QUIZ_CATEGORY,
} from "../../utils/pictureQuizUtils";
import { PICTURE_WORDS_CATEGORY } from "../../utils/pictureWordsUtils";
import {
  ColorblindImage,
  ColorblindImageContainer,
} from "../../components/ColorblindImage";
import { getSlideImages } from "../../utils/slideImages";

/**
 * @fileoverview A full-screen display component for presenting sequences of items with various display options.
 * 
 * @component FullScreenDisplay
 * @description Displays items in a full-screen mode with configurable timing, animations, and interaction modes.
 * Supports both automatic progression and interactive input modes.
 * 
 * @typedef {Object} CollectionItem
 * @property {string} name - The text content of the item
 * @property {string} [svg] - Optional SVG content for visual representation
 * @property {number} [count] - Optional count value for number sense items
 * 
 * @typedef {Object} FullScreenDisplayProps
 * @property {() => void} onEnterFullScreen - Callback function when entering full-screen mode
 * @property {() => void} onExitFullScreen - Callback function when exiting full-screen mode
 * @property {(show: boolean) => void} setShowFeedback - Function to control feedback form visibility
 * 
 * @typedef {Object} FullScreenDisplayState
 * @property {CollectionItem[]} sequence - Array of items to display
 * @property {number} duration - Total duration of the sequence
 * @property {number} speed - Display speed for each item
 * @property {string} textColor - Color of displayed text
 * @property {boolean} shuffle - Whether to randomize the sequence
 * @property {string} category - Category of items being displayed
 * @property {string} type - Type of display sequence
 * @property {number} answerDisplayTime - Duration to show answers
 * @property {number} timerMinutes - Minutes for timer mode
 * @property {number} timerSeconds - Seconds for timer mode
 * @property {string} stopCondition - Condition to end the sequence
 * @property {boolean} isInteractive - Whether the display requires user interaction
 */

interface CollectionItem {
  name: string;
  svg?: string;
  svgs?: string[];
  count?: number;
  answer?: string;
}

interface FullScreenDisplayProps {
  onEnterFullScreen: () => void;
  onExitFullScreen: () => void;
  setShowFeedback: (show: boolean) => void;
}

interface FullScreenDisplayState {
  sequence: Array<{
    name: string;
    svg?: string;
    svgs?: string[];
    count?: number;
    answer?: string;
  }>;
  duration: number;
  speed: number;
  textColor: string;
  shuffle: boolean;
  category: string;
  type: string;
  answerDisplayTime: number;
  timerMinutes: number;
  timerSeconds: number;
  stopCondition: string;
  isInteractive: boolean;
  collectionId?: number;
}

interface SequenceItem {
  name: string;
  isAnswer?: boolean;
  svg?: string;
  svgs?: string[];
  count?: number;
  answer?: string;
}

const FullScreenDisplay: React.FC<FullScreenDisplayProps> = ({
  onEnterFullScreen,
  onExitFullScreen,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  console.log("FullScreenDisplay initialized");
  const {
    sequence,
    speed,
    shuffle,
    category,
    type,
    answerDisplayTime,
    stopCondition: initialStopCondition,
    timerMinutes,
    timerSeconds,
    isInteractive,
    collectionId,
  } = location.state as FullScreenDisplayState;
  console.log("Interactive mode:", isInteractive);
  console.log("Session configuration loaded");
  console.log("Session configuration:", {
    category,
    type,
    sequence: sequence.length,
    isInteractive,
  });
  const { theme } = useTheme();
  const [index, setIndex] = useState(0);
  const [shuffledSequence, setShuffledSequence] = useState<SequenceItem[]>([]);
  const [isPaused, setIsPaused] = useState(false);
  const [intervalId, setIntervalId] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [stopCondition] = useState(initialStopCondition);
  const [isTourRunning, setIsTourRunning] = useState<boolean>(false);
  const [currentTourStep, setCurrentTourStep] = useState<number>(0);
  const [isFeedbackVisible, setIsFeedbackVisible] = useState(false);
  const { isGuidedTourEnabled } = useTour();
  const { updateCompletionCount } = useCompletion();
  const hasRecordedCompletion = useRef(false);
  const intervalIdRef = useRef<number | null>(null);
  const sessionStartRef = useRef<number>(Date.now());
  const seenIndicesRef = useRef<Set<number>>(new Set());
  const missedRef = useRef(0);
  const skippedRef = useRef(0);
  const hasEndedRef = useRef(false);
  const sequenceLengthRef = useRef(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [answerConfettiKey, setAnswerConfettiKey] = useState(0);
  const [userInput, setUserInput] = useState("");
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [itemStartTime, setItemStartTime] = useState<number | null>(null);
  const [itemTimes, setItemTimes] = useState<number[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [totalTime, setTotalTime] = useState<number>(0);
  const [sessionStats, setSessionStats] = useState({
    totalItems: 0,
    timeTaken: 0,
    missedItems: 0,
    skippedItems: 0,
  });

  // Example usage of isInteractive
  if (isInteractive) {
    console.log("Interactive mode is enabled.");
    // You can add more logic here based on isInteractive
  }

  // Create tour steps without visibility states
  const steps = tourStepsFullScreenDisplay();

  const shuffleArray = (array: CollectionItem[]): CollectionItem[] => {
    return [...array].sort(() => Math.random() - 0.5);
  };

  useEffect(() => {
    console.log("Entering FullScreenDisplay with state:", location.state);
    onEnterFullScreen();

    if (sequence && sequence.length > 0) {
      let newShuffledSequence: SequenceItem[];
      if (category === PICTURE_QUIZ_CATEGORY) {
        newShuffledSequence = expandPictureQuizToSlides(sequence, shuffle);
      } else if (shuffle) {
        console.log("Shuffling sequence...");
        newShuffledSequence = shuffleArray(
          sequence.map((item, index) => ({ ...item, id: index })),
        ).map((item, index) => ({
          ...item,
          id: index,
          name: item.name,
          svg: item.svg || "",
          svgs: item.svgs,
          count: item.count || 0,
          isAnswer: false,
        }));
      } else {
        newShuffledSequence = sequence.map((item, index) => ({
          ...item,
          id: index,
          name: item.name,
          svg: item.svg || "",
          svgs: item.svgs,
          count: item.count || 0,
          answer: item.answer,
          isAnswer: false,
        }));
      }
      console.log("New shuffled sequence:", newShuffledSequence);
      setShuffledSequence(newShuffledSequence);
      setShowConfetti(false);
      setAnswerConfettiKey(0);
    } else {
      console.error("Sequence is empty or undefined");
    }

    document.documentElement.style.setProperty(
      "--display-text-color",
      theme.displayTextColor || theme.textColor,
    );
    document.documentElement.style.setProperty(
      "--background-color",
      theme.displayBackgroundColor || theme.backgroundColor,
    );

    return () => {
      console.log("Exiting FullScreenDisplay");
      document.documentElement.style.setProperty(
        "--display-text-color",
        theme.textColor,
      );
      document.documentElement.style.setProperty(
        "--background-color",
        theme.backgroundColor,
      );
      onExitFullScreen();
    };
  }, [
    onEnterFullScreen,
    onExitFullScreen,
    sequence,
    shuffle,
    theme,
    location.state,
  ]);

  // Modify the useEffect that handles answer display timing
  useEffect(() => {
    if (shuffledSequence.length > 0 && !isInteractive) {  // Add isInteractive check
      console.log("Current sequence item:", shuffledSequence[index]);
      if (
        category === "Number Sense" ||
        (category === "Math" && shuffledSequence[index].name.includes("+"))
      ) {
        setShowAnswer(false);
        const timer = setTimeout(() => {
          setShowAnswer(true);
        }, speed);
        return () => clearTimeout(timer);
      }
    }
  }, [shuffledSequence, category, speed, index, isInteractive]);  // Add isInteractive to dependencies

  const recordCompletion = useCallback((stats?: SessionStats) => {
    if (hasRecordedCompletion.current || !collectionId) {
      return;
    }
    hasRecordedCompletion.current = true;
    updateCompletionCount(collectionId, stats);
  }, [collectionId, updateCompletionCount]);

  const leaveSession = useCallback(() => {
    navigate("/your-collections");
  }, [navigate]);

  const handleEndSession = useCallback((didComplete = false) => {
    if (hasEndedRef.current) {
      return;
    }
    hasEndedRef.current = true;
    console.log("Ending session...");

    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
      setIntervalId(null);
    }
    setIsPaused(true);

    if (!didComplete) {
      leaveSession();
      return;
    }

    const uniqueShown = seenIndicesRef.current.size || 1;
    const unshown = Math.max(0, sequenceLengthRef.current - uniqueShown);
    const elapsedMs = Date.now() - sessionStartRef.current;
    const stats: SessionStats = {
      total_items: uniqueShown,
      time_taken: Math.round(elapsedMs / 100) / 10,
      missed_items: missedRef.current,
      skipped_items: skippedRef.current + unshown,
    };

    setSessionStats({
      totalItems: stats.total_items,
      timeTaken: stats.time_taken,
      missedItems: stats.missed_items,
      skippedItems: stats.skipped_items,
    });
    setTotalTime(elapsedMs);
    setShowResults(true);
    setShowConfetti(true);
    recordCompletion(stats);
  }, [leaveSession, recordCompletion]);

  useEffect(() => {
    if (!showAnswer || showResults) {
      return;
    }
    const currentItem = shuffledSequence[index];
    const isStarAnswer =
      category === "Number Sense" ||
      (category === "Math" && Boolean(currentItem?.name.includes("+")));
    if (!isStarAnswer) {
      return;
    }
    setAnswerConfettiKey((key) => key + 1);
  }, [showAnswer, showResults, index, category, shuffledSequence]);

  // Add this function to handle the final item timing
  const getFinalItemDuration = useCallback(() => {
    // If speed is less than 3000ms (3 seconds), use 3000ms for the final item
    return speed < 5000 ? 5000 : speed;
  }, [speed]);

  // Modify the useEffect that handles the interval
  useEffect(() => {
    if (category === PICTURE_QUIZ_CATEGORY) {
      return;
    }
    if (!isInteractive && shuffledSequence.length > 0 && !isPaused) {
      const isMathAnswerItem =
        category === "Number Sense" ||
        (category === "Math" &&
          Boolean(shuffledSequence[0]?.name.includes("+")));

      // A single item is already the last item, so give it the confetti duration
      // instead of advancing at the normal item speed.
      if (shuffledSequence.length === 1 && stopCondition === "collection") {
        const timeout = window.setTimeout(() => {
          handleEndSession(true);
        }, getFinalItemDuration() + (isMathAnswerItem ? answerDisplayTime : 0));
        return () => clearTimeout(timeout);
      }

      if (isMathAnswerItem) {
        const interval = setInterval(() => {
          setIndex((prevIndex) => {
            const newIndex = (prevIndex + 1) % shuffledSequence.length;
            setProgress((newIndex / shuffledSequence.length) * 100);
            setShowAnswer(false);

            // Check if this is the second-to-last item
            if (prevIndex === shuffledSequence.length - 2) {
              // Clear the current interval
              clearInterval(interval);
              // Set up the final item with potentially longer duration
              setTimeout(() => {
                handleEndSession(true);
              }, getFinalItemDuration() + answerDisplayTime);
            }

            setTimeout(() => {
              setShowAnswer(true);
            }, speed);

            if (
              newIndex === 0 &&
              prevIndex !== 0 &&
              stopCondition === "collection"
            ) {
              handleEndSession(true);
            }
            return newIndex;
          });
        }, speed + answerDisplayTime);
        setIntervalId(interval as unknown as number);
        intervalIdRef.current = interval as unknown as number;
        return () => clearInterval(interval);
      } else {
        const interval = setInterval(() => {
          setIndex((prevIndex) => {
            const newIndex = (prevIndex + 1) % shuffledSequence.length;
            setProgress((newIndex / shuffledSequence.length) * 100);

            // Check if this is the second-to-last item
            if (prevIndex === shuffledSequence.length - 2) {
              // Clear the current interval
              clearInterval(interval);
              // Set up the final item with potentially longer duration
              setTimeout(() => {
                handleEndSession(true);
              }, getFinalItemDuration());
            }

            if (
              newIndex === 0 &&
              prevIndex !== 0 &&
              stopCondition === "collection"
            ) {
              handleEndSession(true);
            }
            return newIndex;
          });
        }, speed);
        setIntervalId(interval as unknown as number);
        intervalIdRef.current = interval as unknown as number;
        return () => clearInterval(interval);
      }
    }
  }, [
    shuffledSequence,
    speed,
    isPaused,
    category,
    answerDisplayTime,
    stopCondition,
    handleEndSession,
    getFinalItemDuration,
  ]);

  useEffect(() => {
    if (
      category !== PICTURE_QUIZ_CATEGORY ||
      isInteractive ||
      isPaused ||
      shuffledSequence.length === 0 ||
      showResults
    ) {
      return;
    }

    const currentItem = shuffledSequence[index];
    const duration = currentItem?.isAnswer ? answerDisplayTime : speed;

    const timeout = window.setTimeout(() => {
      if (index >= shuffledSequence.length - 1) {
        handleEndSession(true);
        return;
      }

      const nextIndex = index + 1;
      setIndex(nextIndex);
      setProgress((nextIndex / shuffledSequence.length) * 100);
    }, duration);

    return () => window.clearTimeout(timeout);
  }, [
    category,
    index,
    isInteractive,
    isPaused,
    shuffledSequence,
    speed,
    answerDisplayTime,
    showResults,
    handleEndSession,
  ]);

  useEffect(() => {
    if (
      category !== PICTURE_QUIZ_CATEGORY ||
      !isInteractive ||
      isPaused ||
      shuffledSequence.length === 0 ||
      showResults
    ) {
      return;
    }

    const currentItem = shuffledSequence[index];
    if (!currentItem?.isAnswer) {
      return;
    }

    const timeout = window.setTimeout(() => {
      if (index >= shuffledSequence.length - 1) {
        handleEndSession(true);
        return;
      }

      const nextIndex = index + 1;
      setIndex(nextIndex);
      setProgress((nextIndex / shuffledSequence.length) * 100);
      setItemStartTime(Date.now());
      setUserInput("");
      setIsCorrect(null);
    }, answerDisplayTime);

    return () => window.clearTimeout(timeout);
  }, [
    category,
    index,
    isInteractive,
    isPaused,
    shuffledSequence,
    answerDisplayTime,
    showResults,
    handleEndSession,
  ]);

  useEffect(() => {
    if (stopCondition === "timer" && timerMinutes >= 0 && timerSeconds >= 0) {
      const totalTimerMilliseconds = (timerMinutes * 60 + timerSeconds) * 1000;
      console.log(`Timer set for ${totalTimerMilliseconds}ms`);

      const timerEndTime = Date.now() + totalTimerMilliseconds;

      const checkTimer = setInterval(() => {
        const remainingTime = timerEndTime - Date.now();
        if (remainingTime <= 0) {
          console.log("Timer expired, ending session.");
          clearInterval(checkTimer);
          handleEndSession(true);
        }
      }, 1000);

      return () => {
        clearInterval(checkTimer);
      };
    }
  }, [stopCondition, timerMinutes, timerSeconds, handleEndSession]);

  const handlePauseResume = () => {
    setIsPaused(!isPaused);
    if (isPaused && intervalId) {
      clearInterval(intervalId);
    }
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIndex((prevIndex) => (prevIndex + 1) % shuffledSequence.length);
  };

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIndex(
      (prevIndex) =>
        (prevIndex - 1 + shuffledSequence.length) % shuffledSequence.length,
    );
  };

  // Modify the handleScreenClick function
  const handleScreenClick = () => {
    if (!isInteractive && (
      category === "Number Sense" ||
      (category === "Math" && shuffledSequence[index].name.includes("+"))
    )) {
      console.log("Toggling answer display. Current state:", !showAnswer);
      setShowAnswer(!showAnswer);
    }
  };

  const handleStartTour = () => {
    console.log("Starting Tour");
    setIsTourRunning(true);
    setCurrentTourStep(0); // Reset to the first step
  };

  const handleTourComplete = () => {
    console.log("Tour completed");
    setIsTourRunning(false); // Reset the tour running state
  };

  // Modify the checkAnswer function
  const checkAnswer = () => {
    let correctAnswer = "";
    const currentItem = shuffledSequence[index];

    if (category === PICTURE_QUIZ_CATEGORY) {
      if (currentItem.isAnswer) {
        return;
      }

      correctAnswer = currentItem.answer?.trim() || "";
      const isAnswerCorrect =
        userInput.trim().toLowerCase() === correctAnswer.toLowerCase();
      setIsCorrect(isAnswerCorrect);

      if (!isAnswerCorrect) {
        missedRef.current += 1;
      }

      if (itemStartTime) {
        const timeTaken = Date.now() - itemStartTime;
        setItemTimes((prev) => [...prev, timeTaken]);
      }

      window.setTimeout(() => {
        setUserInput("");
        setIsCorrect(null);

        if (index >= shuffledSequence.length - 1) {
          handleEndSession(true);
          return;
        }

        const nextIndex = index + 1;
        setIndex(nextIndex);
        setProgress((nextIndex / shuffledSequence.length) * 100);
        setItemStartTime(Date.now());
      }, 900);
      return;
    }

    if (category === PICTURE_WORDS_CATEGORY) {
      correctAnswer = currentItem.name?.trim() || "";
      const isAnswerCorrect =
        userInput.trim().toLowerCase() === correctAnswer.toLowerCase();
      setIsCorrect(isAnswerCorrect);

      if (!isAnswerCorrect) {
        missedRef.current += 1;
      }

      if (itemStartTime) {
        const timeTaken = Date.now() - itemStartTime;
        setItemTimes((prev) => [...prev, timeTaken]);
      }

      window.setTimeout(() => {
        setUserInput("");
        setIsCorrect(null);

        if (index >= shuffledSequence.length - 1) {
          handleEndSession(true);
          return;
        }

        const nextIndex = index + 1;
        setIndex(nextIndex);
        setProgress((nextIndex / shuffledSequence.length) * 100);
        setItemStartTime(Date.now());
      }, 900);
      return;
    }

    if (category === "Number Sense") {
      correctAnswer = currentItem.count?.toString() || "";
    } else if (category === "Math") {
      // For math problems, evaluate the expression to get the correct answer
      if (currentItem.name.includes("+")) {
        const [num1, num2] = currentItem.name.split("+").map(n => parseInt(n.trim()));
        correctAnswer = (num1 + num2).toString();
      } else {
        // For simple numbers, use the name property directly
        correctAnswer = currentItem.name;
      }
    } else {
      correctAnswer = currentItem.name;
    }

    const isAnswerCorrect =
      userInput.trim().toLowerCase() === correctAnswer.toLowerCase();
    setIsCorrect(isAnswerCorrect);

    if (isAnswerCorrect) {
      // Show the answer when correct
      setShowAnswer(true);
      
      // Calculate time taken for this item
      if (itemStartTime) {
        const timeTaken = Date.now() - itemStartTime;
        setItemTimes((prev) => [...prev, timeTaken]);
      }

      // Wait for answer display time before moving to next item
      setTimeout(() => {
        setShowAnswer(false);
        
        // If this is the last item, show results
        if (index === shuffledSequence.length - 1) {
          handleEndSession(true);
        } else {
          // Reset for next item
          setItemStartTime(Date.now());
          setUserInput("");
          setIsCorrect(null);
          setIndex((prevIndex) => prevIndex + 1);
          setProgress(((index + 1) / shuffledSequence.length) * 100);
        }
      }, answerDisplayTime);
    } else {
      missedRef.current += 1;
    }
  };

  const skipItem = () => {
    skippedRef.current += 1;
    if (itemStartTime) {
      setItemTimes((prev) => [...prev, Date.now() - itemStartTime]);
    }

    let nextIndex = index + 1;
    if (
      category === PICTURE_QUIZ_CATEGORY &&
      !shuffledSequence[index]?.isAnswer
    ) {
      nextIndex = index + 2;
    }

    if (nextIndex >= shuffledSequence.length) {
      handleEndSession(true);
      return;
    }

    setShowAnswer(false);
    setUserInput("");
    setIsCorrect(null);
    setItemStartTime(Date.now());
    setIndex(nextIndex);
    setProgress((nextIndex / shuffledSequence.length) * 100);
  };

  // Time the session in both auto-play and interactive modes
  useEffect(() => {
    sequenceLengthRef.current = shuffledSequence.length;
    if (shuffledSequence.length === 0 || startTime !== null) {
      return;
    }
    const now = Date.now();
    sessionStartRef.current = now;
    setStartTime(now);
    setItemStartTime(now);
    seenIndicesRef.current = new Set([0]);
  }, [shuffledSequence, startTime]);

  useEffect(() => {
    if (shuffledSequence.length > 0) {
      seenIndicesRef.current.add(index);
    }
  }, [index, shuffledSequence.length]);

  // Add Results component
  const ResultsDisplay = () => {
    const formatTime = (ms: number) => {
      const seconds = Math.floor(ms / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    };

    const formatSeconds = (seconds: number) => {
      const total = Math.max(0, Math.round(seconds));
      const minutes = Math.floor(total / 60);
      const remainingSeconds = total % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    };

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
        <div className="w-[min(92vw,28rem)] rounded-lg bg-white p-8 text-black">
          <h2 className="mb-4 text-2xl font-bold">Session Report</h2>
          <p className="mb-2">Items shown: {sessionStats.totalItems}</p>
          <p className="mb-2">
            Time taken: {formatSeconds(sessionStats.timeTaken)} (
            {sessionStats.timeTaken.toFixed(1)} seconds)
          </p>
          <p className="mb-2">Missed items: {sessionStats.missedItems}</p>
          <p className="mb-4">Skipped items: {sessionStats.skippedItems}</p>
          {itemTimes.length > 0 && (
            <>
              <p className="mb-2">Total Time: {formatTime(totalTime)}</p>
              <p className="mb-4">
                Average Time per Item:{" "}
                {formatTime(totalTime / Math.max(1, itemTimes.length))}
              </p>
              <div className="mb-4 max-h-40 overflow-y-auto">
                {itemTimes.map((time, idx) => (
                  <p key={idx}>
                    Item {idx + 1}: {formatTime(time)}
                  </p>
                ))}
              </div>
            </>
          )}
          <button
            type="button"
            onClick={leaveSession}
            className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
          >
            Return to Collections
          </button>
        </div>
      </div>
    );
  };

  // Add this check at the beginning of the component
  if (!shuffledSequence.length) {
    return <div>Loading...</div>;
  }

  console.log("Rendering with index:", index);
  console.log("Current item:", shuffledSequence[index]);

  const getTextClass = (text: string) => {
    const baseClass = "font-[var(--display-font-family)] ";
    if (text.length <= 4) return baseClass + "text-[clamp(25vw,30vw,35vw)]";
    if (text.length <= 7) return baseClass + "text-[clamp(15vw,19vw,23vw)]";
    if (text.length <= 12) return baseClass + "text-[clamp(8vw,12vw,16vw)]";
    if (text.length <= 15) return baseClass + "text-[clamp(5vw,9vw,13vw)]";
    return baseClass + "text-[clamp(2vw,6vw,10vw)]";
  };

  const renderSlideImages = (
    sources: string[],
    alt: string,
    maxHeight = "min(55vh, 70vw)",
  ) => {
    if (sources.length === 0) {
      return null;
    }

    if (sources.length === 1) {
      return (
        <div
          className="flex w-full items-center justify-center"
          style={{ maxHeight }}
        >
          <ColorblindImage
            src={sources[0]}
            alt={alt}
            className="max-h-full max-w-full object-contain"
            onError={(e) => {
              console.error("Error loading picture image:", e);
            }}
          />
        </div>
      );
    }

    return (
      <div
        className="grid w-full grid-cols-2 items-center gap-4"
        style={{ height: maxHeight, maxHeight }}
      >
        {sources.map((src, imageIndex) => (
          <div
            key={`${alt}-${imageIndex}`}
            className="flex h-full min-w-0 items-center justify-center"
          >
            <ColorblindImage
              src={src}
              alt={`${alt} ${imageIndex + 1}`}
              className="max-h-full max-w-full object-contain"
              onError={(e) => {
                console.error("Error loading picture image:", e);
              }}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderPictureWithLabelBelow = (
    sources: string[],
    alt: string,
    label: string,
    maxHeight = "min(55vh, 70vw)",
  ) => (
    <div className="flex w-full max-w-[min(90vw,85vh)] flex-col items-center gap-4">
      {renderSlideImages(sources, alt, maxHeight)}
      <div className="w-full shrink-0 rounded-lg bg-black/80 px-4 py-3 text-center">
        <p className="break-words text-[clamp(1.25rem,3.5vw,2.75rem)] font-bold leading-tight text-white">
          {label}
        </p>
      </div>
    </div>
  );

  const renderInteractiveAnswerControls = () => (
    <div
      className="interactive-answer relative z-20 flex w-full max-w-xs shrink-0 flex-col items-stretch gap-2 sm:max-w-sm"
      onClick={(event) => event.stopPropagation()}
    >
      <input
        type="text"
        value={userInput}
        onChange={(e) => setUserInput(e.target.value)}
        placeholder="Type your answer"
        className="min-h-[44px] w-full rounded border border-black px-3 py-2 text-base"
        onFocus={(e) => {
          e.currentTarget.scrollIntoView({ block: "center", behavior: "smooth" });
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            checkAnswer();
          }
        }}
        autoFocus
      />
      <button
        type="button"
        onClick={checkAnswer}
        className="bg-custom-blue hover:bg-custom-blue-dark min-h-[44px] rounded border border-black px-3 py-2 text-base font-bold text-white transition-colors duration-300"
      >
        Submit Answer
      </button>
      <button
        type="button"
        onClick={skipItem}
        className="skip-button min-h-[44px] rounded border border-black bg-yellow-500 px-3 py-2 text-base font-bold text-black transition-colors duration-300 hover:bg-yellow-600"
      >
        Skip
      </button>
      {isCorrect !== null && (
        <div>
          {isCorrect ? (
            <p className="text-green-500">Correct!</p>
          ) : category === PICTURE_QUIZ_CATEGORY ? (
            <p className="text-red-500">Not quite — showing the answer next</p>
          ) : category === PICTURE_WORDS_CATEGORY ? (
            <p className="text-red-500">
              Not quite — it was: {shuffledSequence[index]?.name}
            </p>
          ) : (
            <p className="text-red-500">Incorrect, try again!</p>
          )}
        </div>
      )}
    </div>
  );

  const renderInteractivePictureLayout = (
    prompt: string | null,
    sources: string[],
    alt: string,
  ) => (
    <div className="absolute inset-0 z-10 flex min-h-0 flex-col items-center justify-start overflow-y-auto px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(4rem,env(safe-area-inset-top))]">
      <div className="flex w-full max-w-[min(90vw,100%)] flex-col items-center gap-4 py-2">
        {prompt ? (
          <p className="picture-quiz-prompt max-w-full shrink-0 text-center text-[clamp(1.25rem,3.5vw,2.5rem)] font-bold leading-tight">
            {prompt}
          </p>
        ) : null}
        {sources.length > 0
          ? renderSlideImages(sources, alt, "min(42vh, 55vw)")
          : null}
        {renderInteractiveAnswerControls()}
      </div>
    </div>
  );

  const showFloatingInteractiveControls =
    isInteractive &&
    category !== PICTURE_QUIZ_CATEGORY &&
    category !== PICTURE_WORDS_CATEGORY;

  const renderPictureWordsImage = (
    sources: string[],
    word: string,
    showWordOverlay: boolean,
    maxHeight = "min(55vh, 70vw)",
  ) =>
    showWordOverlay
      ? renderPictureWithLabelBelow(sources, word, word, maxHeight)
      : renderSlideImages(sources, word, maxHeight);

  const renderContent = () => {
    const currentItem = shuffledSequence[index];

    if (category === PICTURE_WORDS_CATEGORY) {
      const word = currentItem.name?.trim() || "";
      const sources = getSlideImages(currentItem);

      if (isInteractive) {
        return renderInteractivePictureLayout(null, sources, word);
      }

      return (
        <div className="flex size-full flex-col items-center justify-center px-4 py-6">
          {sources.length > 0 ? (
            renderPictureWordsImage(
              sources,
              word,
              true,
              "min(50vh, 65vw)",
            )
          ) : (
            <p className="max-w-[90vw] break-words text-center text-[clamp(1.5rem,4vw,3rem)] font-bold">
              {word}
            </p>
          )}
        </div>
      );
    }

    if (category === PICTURE_QUIZ_CATEGORY) {
      const sources = getSlideImages(currentItem);

      if (!currentItem.isAnswer) {
        if (isInteractive) {
          return renderInteractivePictureLayout(
            currentItem.name,
            sources,
            currentItem.name,
          );
        }

        return (
          <div className="flex size-full flex-col items-center justify-center gap-4 px-4 py-6">
            <p className="picture-quiz-prompt max-w-[90vw] shrink-0 text-center text-[clamp(1.5rem,4vw,3rem)] font-bold">
              {currentItem.name}
            </p>
            {sources.length > 0
              ? renderSlideImages(sources, currentItem.name, "min(50vh, 65vw)")
              : null}
          </div>
        );
      }

      const answerText =
        currentItem.answer?.trim() || currentItem.name?.trim() || "";

      return (
        <div className="flex size-full flex-col items-center justify-center px-4 py-6">
          {sources.length > 0 ? (
            renderPictureWithLabelBelow(
              sources,
              answerText,
              answerText,
              "min(50vh, 65vw)",
            )
          ) : (
            <p className="max-w-[90vw] break-words text-center text-[clamp(1.5rem,4vw,3rem)] font-bold">
              {answerText}
            </p>
          )}
        </div>
      );
    }

    if (category === "Number Sense") {
      const prompt = isNumberSenseQuestion(currentItem.name)
        ? currentItem.name
        : "";
      const sources = getSlideImages(currentItem);
      return (
        <div className="flex size-full flex-col items-center justify-center gap-4">
          {!showAnswer && (
            <>
              {prompt ? (
                <p className="number-sense-prompt shrink-0 px-4 text-center text-[clamp(1.5rem,4vw,3rem)] font-bold">
                  {prompt}
                </p>
              ) : null}
              {sources.length > 1 ? (
                <div className="flex min-h-0 w-full flex-1 items-center justify-center px-4">
                  {renderSlideImages(
                    sources,
                    currentItem.name,
                    "min(60vh, 80vw)",
                  )}
                </div>
              ) : sources.length === 1 ? (
                <div className="flex min-h-0 flex-1 items-center justify-center">
                  <ColorblindImage
                    src={sources[0]}
                    alt={currentItem.name}
                    style={{ maxWidth: "100%", maxHeight: "100%" }}
                    onError={(e) => {
                      console.error("Error loading image:", e);
                      console.log("Attempted SVG source:", sources[0]);
                    }}
                  />
                </div>
              ) : null}
            </>
          )}

          {showAnswer && (
            <div className="flex flex-col items-center gap-2">
              {prompt ? (
                <p className="px-4 text-center text-[clamp(1.25rem,3vw,2.25rem)] font-bold">
                  {prompt}
                </p>
              ) : null}
              <div className="relative">
                <svg viewBox="0 0 200 200" className="size-64">
                  <polygon
                    points="100,10 40,180 190,60 10,60 160,180"
                    fill="yellow"
                    stroke="orange"
                    strokeWidth="5"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-6xl font-bold text-orange-600">
                    {currentItem.count}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    } else if (category === "Math" && currentItem.name.includes("+")) {
      return (
        <div className="flex size-full flex-col items-center justify-center gap-4">
          <h1
            className={`max-w-[90vw] break-words text-center leading-tight transition-all duration-300 ${getTextClass(currentItem.name)}`}
          >
            {currentItem.name}
          </h1>

          {showAnswer && (
            <div className="relative">
              <svg viewBox="0 0 200 200" className="size-64">
                <polygon
                  points="100,10 40,180 190,60 10,60 160,180"
                  fill="yellow"
                  stroke="orange"
                  strokeWidth="5"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-6xl font-bold text-orange-600">
                  {currentItem.count}
                </span>
              </div>
            </div>
          )}
        </div>
      );
    } else if (category === "Choose File" || currentItem.svg) {
      const sources = getSlideImages(currentItem);
      if (sources.length > 1) {
        return (
          <div className="grid h-dvh w-full grid-cols-2 items-center gap-4 px-4 py-6">
            {sources.map((src, imageIndex) => (
              <ColorblindImage
                key={`${currentItem.name}-${imageIndex}`}
                src={src}
                alt={`${currentItem.name} ${imageIndex + 1}`}
                className="max-h-[90vh] w-full object-contain"
              />
            ))}
          </div>
        );
      }

      return (
        <div className="flex size-full items-center justify-center">
          {currentItem.svg?.startsWith("data:application/pdf") ? (
            <object
              data={currentItem.svg}
              type="application/pdf"
              className="size-full"
              style={{
                width: "100%",
                height: "100vh",
                border: "none",
              }}
            >
              <p>
                Unable to display PDF file.{" "}
                <a href={currentItem.svg} download={currentItem.name}>
                  Download
                </a>{" "}
                instead.
              </p>
            </object>
          ) : (
            <ColorblindImage
              src={currentItem.svg}
              alt={currentItem.name}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          )}
        </div>
      );
    } else if (category === "Science" && type === "periodicTable") {
      const [symbol, name, atomicNumber] = currentItem.name.split(" - ");
      return (
        <div className="flex h-full flex-col items-center justify-center">
          <h1 className={`m-0 ${getTextClass(atomicNumber)}`}>
            {atomicNumber}
          </h1>
          <h2 className={`m-0 ${getTextClass(name)}`}>{name}</h2>
          <h3 className={`m-0 ${getTextClass(symbol)}`}>{symbol}</h3>
        </div>
      );
    } else if (category === "Domino Comparison") {
      return (
        <div className="flex flex-col items-center justify-center">
          <ColorblindImageContainer>
            <div dangerouslySetInnerHTML={{ __html: currentItem.svg || "" }} />
          </ColorblindImageContainer>
          
          <div className="mt-4 flex space-x-4">
            <button 
              className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
              onClick={() => handleAnswer("left")}
            >
              Left Side
            </button>
            <button 
              className="rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
              onClick={() => handleAnswer("equal")}
            >
              Equal
            </button>
            <button 
              className="rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600"
              onClick={() => handleAnswer("right")}
            >
              Right Side
            </button>
          </div>
        </div>
      );
    } else {
      return (
        <h1
          className={`max-w-[90vw] break-words text-center leading-tight transition-all duration-300 ${getTextClass(currentItem.name)}`}
        >
          {currentItem.name}
        </h1>
      );
    }
  };

  const handleAnswer = (answer: string) => {
    const currentItem = shuffledSequence[index];
    
    // Check if the answer is correct
    if (currentItem.name === answer) {
      // Handle correct answer
      setIsCorrect(true);
      // Maybe play a sound or show a message
    } else {
      // Handle incorrect answer
      setIsCorrect(false);
    }
    
    // Move to the next item after a short delay
    setTimeout(() => {
      handleNext({} as React.MouseEvent<Element, MouseEvent>);
      setIsCorrect(null); // Reset the correct/incorrect state
    }, 1000);
  };

  console.log("isInteractive:", isInteractive);

  return (
    <div
      className="fullscreen-display relative m-0 flex h-[100dvh] max-h-[100dvh] w-full max-w-[100dvw] items-center justify-center overflow-hidden p-0 transition-colors duration-300"
      style={{
        color: theme.displayTextColor || theme.textColor,
        backgroundColor: theme.displayBackgroundColor || theme.backgroundColor,
      }}
      onClick={handleScreenClick}
    >
      {/* Confetti effect */}
      {showConfetti && (
        <Confetti
          style={{ pointerEvents: "none", zIndex: 5 }}
        />
      )}
      {answerConfettiKey > 0 && !showResults && (
        <Confetti
          key={answerConfettiKey}
          recycle={false}
          numberOfPieces={220}
          gravity={0.28}
          style={{ pointerEvents: "none", zIndex: 5 }}
        />
      )}

      {/* Control buttons in top-left corner */}
      <div className="session-controls safe-top safe-left absolute z-50 flex flex-col gap-2">
        <button
          type="button"
          className="end-session-button bg-custom-red hover:bg-custom-red-dark rounded border border-black px-3 py-1.5 text-sm font-bold text-white transition-colors duration-300"
          onClick={() => handleEndSession()}
        >
          Back
        </button>
        <button
          type="button"
          className="pause-button bg-custom-green hover:bg-custom-green-dark rounded border border-black px-3 py-1.5 text-sm font-bold text-white transition-colors duration-300"
          onClick={handlePauseResume}
        >
          {isPaused ? "Resume" : "Pause"}
        </button>
      </div>

      {/* Tour and Feedback buttons in top-right corner */}
      <div className="safe-top safe-right absolute z-50 flex flex-col gap-2">
        {isGuidedTourEnabled && (
          <button
            type="button"
            onClick={handleStartTour}
            className="rounded border border-black bg-blue-500 px-2 py-1 text-xs font-medium text-white transition-colors duration-300 hover:bg-blue-600"
          >
            Start Full Screen Display Tour
          </button>
        )}
        <div className="hover:scale-160 scale-150 transition-transform">
          <FeedbackIcon onClick={() => setIsFeedbackVisible(true)} />
        </div>
      </div>

      {/* Main content */}
      {renderContent()}

      {/* Navigation buttons */}
      {!isInteractive && (
        <>
          <button
            type="button"
            className="previous-button absolute left-2 top-1/2 z-40 flex size-12 -translate-y-1/2 items-center justify-center rounded-full border border-black bg-black/50 pb-[4px] text-3xl text-white transition-colors duration-300 hover:bg-black/70 sm:left-5 sm:size-16 sm:text-5xl"
            onClick={handlePrevious}
          >
            ←
          </button>
          <button
            type="button"
            className="next-button absolute right-2 top-1/2 z-40 flex size-12 -translate-y-1/2 items-center justify-center rounded-full border border-black bg-black/50 pb-[4px] text-3xl text-white transition-colors duration-300 hover:bg-black/70 sm:right-5 sm:size-16 sm:text-5xl"
            onClick={handleNext}
          >
            →
          </button>
        </>
      )}

      {/* Progress indicator */}
      <div className="progress-indicator safe-bottom fixed inset-x-2.5 h-2.5 rounded-full border border-black bg-white/30">
        <div
          className="h-full rounded-full border border-black transition-all duration-300 ease-in-out"
          style={{
            width: `${progress}%`,
            backgroundColor: progress === 100 ? "gold" : "green",
          }}
        ></div>
      </div>
      <div
        className="fixed left-2.5 text-xs"
        style={{
          bottom: "calc(max(0.625rem, env(safe-area-inset-bottom, 0px)) + 1.25rem)",
          color: theme.displayTextColor || theme.textColor,
        }}
      >
        {Math.round(progress)}% Complete
      </div>

      {/* Tour and Feedback components */}
      {isGuidedTourEnabled && (
        <GuidedTour
          steps={steps}
          isRunning={isTourRunning}
          onComplete={handleTourComplete}
          currentStep={currentTourStep}
          onStepChange={setCurrentTourStep}
          tourName="fullScreenDisplay"
        />
      )}

      {isFeedbackVisible && (
        <FeedbackForm onClose={() => setIsFeedbackVisible(false)} />
      )}

      {/* Interactive Mode Elements - Only show when isInteractive is true */}
      {showFloatingInteractiveControls && (
        <div className="absolute bottom-20">{renderInteractiveAnswerControls()}</div>
      )}

      {showResults && <ResultsDisplay />}
    </div>
  );
};

export default FullScreenDisplay;
